// CommandPalette - the header search box. Real search over pages + live data.
//
// G360_K886 - THIS USED TO BE A MODAL, AND THE HEADER BOX WAS A DECOY.
// The shared AppShell has rendered a search input in the header since the
// shell shipped. This component intercepted clicks on it in the CAPTURE
// phase, called preventDefault and opened a centred modal instead:
//
//   const onDocClick = (e) => { if (e.target.closest('.ap-search')) { ... } }
//   document.addEventListener('click', onDocClick, true);
//
// So the header input could never take focus, on any page - "why does
// search always pop up instead of letting me type there". Worse, BOTH
// components bound Ctrl-K: AppShell's handler focused the input nobody
// could see while this one opened the modal over the top of it.
//
// There is now one search, and it lives where people already click. This
// component renders the header box itself (passed to AppShell as
// searchSlot) and keeps everything the modal had that the header box
// never did: live tenant data, per-organisation scoping, honest
// reporting of sources that could not be read, and keyboard navigation.
// AppShell's own menu-only search remains as its standalone fallback for
// any app that passes no slot.
//
// G360_K461 - this organisation's records. Tools, policies, risks and
// incidents were read with no tenant, so a member of two organisations (and
// staff) searched both and opened the other one's record into this one's
// page. They are now read for the organisation on screen, and the cache is
// kept per organisation.
//
// Searches:
//   - Pages (the nav routes) -> navigate by hash
//   - AI tools, policies, risks, incidents (live, RLS-scoped) -> navigate to the page
//
// Note: navigation is per-PAGE (the app routes by hash, not per-record), so a
// result opens the right page. Where a page reads ?q=, we also pass the term.

import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, CornerDownLeft, Hash, Radar, SlidersHorizontal, AlertTriangle, Flag } from 'lucide-react';
import { useAuth, supabase } from '@govern360/shared';
import { listPolicies } from '../lib/policyApi.js';
import { listIncidents } from '../lib/grcApi.js';

// One table for one organisation, the first 1,000 rows - a search box, not
// a report.
async function readFor(table, tenantId, order) {
  const { data, error } = await supabase.from(table).select('*')
    .eq('tenant_id', tenantId).order(order, { ascending: false }).limit(1000);
  if (error) throw error;
  return data || [];
}

// G360_K715 - THE PAGES ARE THE MENU. They used to be a hand-kept list of 25
// names that had fallen behind the sidebar: Connections, Governance Register,
// Token Governance, Endpoint Governance and thirty more could not be found.
// They now come from the sidebar itself (navGroups from ConsoleApp, already
// filtered to what this role may open), so a page added or renamed in the
// menu is found the same day. Matching is per word, ignores case and word
// endings (connector finds Connections), and reads what each page is for
// (SYN below), so a page is found by what it does as well as by its name.
const EXTRA_PAGES = [
  { id: 'documentation', label: 'Documentation', route: 'documentation', group: 'Help' },
  { id: 'faq', label: 'FAQ', route: 'faq', group: 'Help' },
];
const SYN = {
  zerotrust: 'zero trust ztmm cisa pillars maturity',
  execboard: 'board executive summary directors',
  execcaio: 'caio chief ai officer executive',
  execcio: 'cio chief information officer executive',
  execciso: 'ciso chief information security officer security executive',
  execcfo: 'cfo spend cost finance budget executive',
  execcfoval: 'cfo value roi return benefit finance executive',
  execcro: 'cro chief risk officer executive',
  exposurescore: 'exposure score posture rating',
  starthere: 'decisions start here queue next actions todo',
  dashboard: 'home overview summary',
  ainews: 'news articles',
  inventory: 'inventory tools apps shadow catalog devices endpoints laptops computers',
  discovery: 'discovery discover scan scans candidates shadow triage',
  agents: 'agents models copilot studio agent builder llm foundation registry all agents built agents',
  nhi: 'nhi non human identity identities service principals credentials secrets app registrations',
  exposurepaths: 'exposure paths attack path trifecta exfiltration',
  govregister: 'governance register decisions accept defer dismiss register',
  releasecert: 'release certification approve approval certify',
  risks: 'risk register risks registry findings',
  owasp: 'owasp top 10 llm asi agentic',
  myagents: 'my agents your agents owned',
  behrisk: 'behavioral behaviour behavior anomaly anomalies',
  dataposture: 'data posture purview labels sensitivity dlp sharepoint',
  vendors: 'vendor vendors third party supplier suppliers',
  tokengov: 'token tokens governance spend cost budget budgets usage keys api keys openai anthropic ramp concur',
  copilotconsum: 'copilot consumption credits seats licences licenses m365 usage',
  purposereg: 'purpose register purposes sign attest attestation',
  archreview: 'architecture review design review assessment',
  supervision: 'supervision health human in the loop oversight',
  accessreq: 'access requests request approvals',
  routingrules: 'approval routing rules approvers',
  incidents: 'incidents incident response cases',
  policies: 'policies policy rules prevention dlp prompt',
  datashield: 'data shield dlp redact redaction sensitive',
  mcpgw: 'mcp gateway model context protocol servers tools',
  netblock: 'network block list firewall palo alto prisma edl',
  purview: 'purview microsoft dlp labels',
  deploy: 'endpoint governance deploy guard extension browser prompt guard devices endpoints intune install inventory collector deploy policies',
  coverage: 'enforcement coverage planes matrix',
  compliance: 'compliance enforcement eu ai act frameworks controls iso nist soc',
  reports: 'reports report export pdf',
  audit: 'audit log logs history events trail',
  integrations: 'connector connectors integration apps marketplace webhook slack teams jira',
  connections: 'connector connectors connect integration source sources data sources microsoft entra azure aws google okta github salesforce servicenow sync',
  siem: 'siem streaming splunk sentinel webhook export events',
  users: 'users roles team members people invite permissions',
  security: 'security settings sso mfa session password',
  govrules: 'alert rules alerts notifications review rules',
  g360copilot: 'g360 copilot assistant chat',
  branding: 'branding logo colours colors theme',
  billing: 'billing invoice plan subscription payment',
  implguide: 'implementation guide setup onboarding',
  adminguide: 'administrator guide admin guide manual documentation',
  help: 'help support contact ticket',
  account: 'my account profile password settings',
  documentation: 'documentation docs manual guide',
  faq: 'faq questions answers',
};
function words(s) { return String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean); }
function stem(w) {
  let s = String(w || '').toLowerCase();
  const suf = ['ations', 'ation', 'ions', 'ion', 'ings', 'ing', 'ors', 'or', 'ers', 'er', 'ies', 'es', 's'];
  for (const x of suf) { if (s.length - x.length >= 4 && s.endsWith(x)) { s = s.slice(0, -x.length); break; } }
  return s;
}
function buildPages(navGroups) {
  const out = []; const seen = {};
  (navGroups || []).forEach((g) => (g.items || []).forEach((it) => {
    if (!it || !it.route || seen[it.route]) return;
    seen[it.route] = 1;
    const hay = [it.label, g.label, it.route, (it.also || []).join(' '), SYN[it.id] || ''].join(' ').toLowerCase();
    out.push({ id: it.id, label: it.label, route: it.route, group: g.label, hay, stems: words(hay).map(stem) });
  }));
  EXTRA_PAGES.forEach((p) => {
    if (seen[p.route]) return;
    const hay = [p.label, p.group, p.route, SYN[p.id] || ''].join(' ').toLowerCase();
    out.push({ ...p, hay, stems: words(hay).map(stem) });
  });
  return out;
}
function matchPages(pages, needle) {
  const n = String(needle || '').trim().toLowerCase();
  const terms = words(n);
  if (!terms.length) return [];
  const out = [];
  for (const p of pages) {
    const ok = terms.every((t) => {
      if (p.hay.indexOf(t) >= 0) return true;
      const ts = stem(t);
      return p.stems.some((w) => w.startsWith(ts) || (w.length >= 4 && ts.startsWith(w)));
    });
    if (!ok) continue;
    const lab = p.label.toLowerCase();
    const labStems = words(p.label).map(stem);
    let score = 4;
    if (terms.every((t) => labStems.some((w) => w.startsWith(stem(t))))) score = 3;
    if (lab.indexOf(n) >= 0) score = 2;
    if (lab.startsWith(n)) score = 1;
    out.push({ label: p.label, route: p.route, group: p.group, score });
  }
  out.sort((a, b) => a.score - b.score || a.label.localeCompare(b.label));
  return out.slice(0, 8);
}

export default function CommandPalette({ navigate, navGroups }) {
  const { tenant } = useAuth();
  const tid = (tenant && tenant.id) || null;
  const [q, setQ] = useState('');
  /* Dismissed, not "closed": the dropdown follows the text, so a click
     away or Escape has to suppress it without throwing away what the
     person typed. Any keystroke brings it back. */
  const [dismissed, setDismissed] = useState(false);
  /* G360_K886 - the four table reads used to fire when the modal opened.
     The header box is on screen from the first paint, so reading on mount
     would run four queries per page load for everyone, searching or not.
     They fire on the first focus instead - the moment somebody intends to
     search, and still before they have finished typing a word. */
  const [primed, setPrimed] = useState(false);
  const [results, setResults] = useState({ pages: [], tools: [], policies: [], risks: [], incidents: [] });
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  /* G360_K886 - bumped when the four reads land. The recompute below
     depends on it, because the data effect's own closure holds the query
     as it was when the box was FOCUSED - empty. Calling compute(q) from
     inside that effect therefore wiped results the user had already typed
     for: live policies, tools, risks and incidents never appeared unless
     you typed another character after the data arrived. Caught by the
     browser test, not by reading it. The query is now read where it is
     current rather than where it was captured. */
  const [dataVersion, setDataVersion] = useState(0);
  // G360_K468 - which data sources could not be searched this time.
  const [unsearched, setUnsearched] = useState([]);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const cache = useRef(null); // {tid, tools, policies, risks, incidents}
  // G360_K715 - the menu as searchable pages, rebuilt when the menu changes.
  const pagesRef = useRef([]);
  pagesRef.current = buildPages(navGroups);

  /* Ctrl-K focuses this box. It is the only Ctrl-K handler now: AppShell
     skips its own when a slot is supplied, so the two no longer fight.
     'g360-open-search' is kept for anything that wants to send people
     here by name. */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        setDismissed(false);
        setPrimed(true);
        if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
      }
    };
    const onOpen = () => {
      setDismissed(false);
      setPrimed(true);
      if (inputRef.current) inputRef.current.focus();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('g360-open-search', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('g360-open-search', onOpen);
    };
  }, []);

  /* Click away closes the list. On mousedown, and NOT in the capture
     phase - capturing document clicks to drive this box is the exact
     mistake this kit removes. boxRef wraps the input and the dropdown, so
     clicking a result is inside and survives. */
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setDismissed(true);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Load live data once primed, and again if the organisation changes.
  useEffect(() => {
    if (!primed || !tid || (cache.current && cache.current.tid === tid)) return;
    setLoading(true);
    /* G360_K468 - a source that could not be read used to become an empty
       list, cached for the session: "No matches" when the truth was "not
       searched". The failed sources are named under the results, and the
       cache is not kept, so searching again retries them. */
    const failed = [];
    const miss = (label) => () => { failed.push(label); return []; };
    const dataOf = (label) => (r) => { if (r && r.error) { failed.push(label); return []; } return (r && r.data) || []; };
    Promise.all([
      readFor('ai_tools', tid, 'user_count').catch(miss('AI tools')),
      listPolicies(tid).then(dataOf('policies')).catch(miss('policies')),
      readFor('risks', tid, 'created_at').catch(miss('risks')),
      listIncidents(tid).then(dataOf('incidents')).catch(miss('incidents')),
    ]).then(([tools, policies, risks, incidents]) => {
      cache.current = { tid: failed.length ? null : tid, tools: tools || [], policies: policies || [], risks: risks || [], incidents: incidents || [] };
      setUnsearched(failed);
      setLoading(false);
      setDataVersion((v) => v + 1); // G360_K886 - recompute with the CURRENT query, below
    });
  }, [primed, tid]); // eslint-disable-line -- the recompute is driven by dataVersion, not by q here

  const compute = useCallback((term) => {
    const t = term.trim().toLowerCase();
    const c = cache.current || { tools: [], policies: [], risks: [], incidents: [] };
    /* G360_K886 - the same per-word, stem-aware match the PAGES use.
       Pages and records now sit in one dropdown, and with a plain
       substring test on the records, typing "policies" returned the
       Policies page and not one of this organisation's policies - the
       same word, two answers, in the same list. The whole-phrase
       substring is tried first, so everything that matched before still
       matches: this only ADDS results. Same stemming as K881. */
    const terms = words(t);
    const m = (...fields) => {
      const hay = fields.filter(Boolean).map(String).join(' ').toLowerCase();
      if (!hay) return false;
      if (hay.indexOf(t) >= 0) return true;
      const ws = words(hay).map(stem);
      return terms.length > 0 && terms.every((x) => {
        const xs = stem(x);
        return ws.some((w) => w.startsWith(xs) || (w.length >= 4 && xs.startsWith(w)));
      });
    };
    const pages = t ? matchPages(pagesRef.current, t) : []; // G360_K715
    if (!t) { setResults({ pages, tools: [], policies: [], risks: [], incidents: [] }); setActive(0); return; }
    setResults({
      pages,
      tools: c.tools.filter((x) => m(x.name, x.vendor)).slice(0, 5),
      policies: c.policies.filter((x) => m(x.name, x.description)).slice(0, 5),
      risks: c.risks.filter((x) => m(x.title)).slice(0, 5),
      incidents: c.incidents.filter((x) => m(x.title)).slice(0, 5),
    });
    setActive(0);
  }, []);

  useEffect(() => { compute(q); }, [q, dataVersion, compute]);

  /* G360_K886 - sections first, then the flat list DERIVED from them.
     The modal built both by hand in the same order and relied on them
     staying in step for the highlight to point at the row it ran Enter
     on. Deriving one from the other makes that impossible to get wrong. */
  const SECTIONS = [
    ['Pages', results.pages.map((p) => ({ kind: 'page', label: p.label, grp: p.group, route: p.route, icon: Hash }))],
    ['AI tools', results.tools.map((x) => ({ kind: 'tool', label: x.name, sub: x.vendor, route: 'inventory', icon: Radar }))],
    ['Policies', results.policies.map((x) => ({ kind: 'policy', label: x.name, route: 'policies', icon: SlidersHorizontal }))],
    ['Risks', results.risks.map((x) => ({ kind: 'risk', label: x.title, sub: x.severity, route: 'risks', icon: AlertTriangle }))],
    ['Incidents', results.incidents.map((x) => ({ kind: 'incident', label: x.title, sub: x.severity, route: 'incidents', icon: Flag }))],
  ].filter(([, items]) => items.length);
  const flat = SECTIONS.flatMap(([, items]) => items);

  const term = q.trim();
  const showRes = term !== '' && !dismissed;

  const choose = (item) => {
    if (!item) return;
    setQ(''); setActive(0); setDismissed(false);
    if (inputRef.current) inputRef.current.blur();
    if (navigate) navigate(item.route); else window.location.hash = `#${item.route}`;
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setDismissed(false); setActive((a) => Math.min(a + 1, Math.max(flat.length - 1, 0))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (flat.length) choose(flat[active]); }
    else if (e.key === 'Escape') { setQ(''); setDismissed(true); if (inputRef.current) inputRef.current.blur(); }
  };

  /* The whole box, including the dropdown, so AppShell's CSS applies
     unchanged and boxRef has something to test clicks against. */
  let idx = -1;
  return (
    <div className="ap-search" ref={boxRef}>
      <Search size={20} />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setDismissed(false); }}
        onKeyDown={onKeyDown}
        onFocus={() => setPrimed(true)}
        placeholder="Search pages, tools, policies, risks, incidents"
        aria-label="Search Govern360"
        autoComplete="off"
        data-testid="header-search"
      />
      <kbd>Ctrl K</kbd>
      {showRes && (
        <div className="ap-search-res" data-testid="header-search-results">
          {/* G360_K886 - pages match with no data at all, so they show at
              once and this line says only what is still missing. The modal
              replaced everything with "Loading..." and looked like it had
              found nothing. */}
          {loading && (
            <div className="ap-search-note">Your tools, policies, risks and incidents are still loading.</div>
          )}
          {/* G360_K468 */}
          {!loading && unsearched.length > 0 && (
            <div className="ap-search-note">Not searched - could not be read: {unsearched.join(', ')}.</div>
          )}
          {flat.length === 0 && !loading && (
            <div className="ap-search-empty">No matches for &quot;{term}&quot; in pages, AI tools, policies, risks or incidents.</div>
          )}
          {SECTIONS.map(([title, items]) => (
            <div key={title}>
              <div className="ap-search-sec">{title}</div>
              {items.map((item) => {
                idx += 1; const i = idx; const Icon = item.icon;
                return (
                  <button
                    key={`${item.kind}-${item.label}-${i}`}
                    type="button"
                    className={`ap-search-item ${i === active ? 'hi' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(item)}
                  >
                    {Icon && <Icon size={16} />}
                    {/* G360_K886 - name and qualifier share ONE growing cell.
                        With both the group and the Enter hint carrying
                        margin-left:auto, flexbox split the free space between
                        them and parked the group halfway across the row. */}
                    <span className="ap-search-main">
                      <span className="ap-search-lab">{item.label}</span>
                      {item.sub ? <span className="ap-search-sub">{item.sub}</span> : null}
                    </span>
                    {item.grp ? <span className="ap-search-grp">{item.grp}</span> : null}
                    {i === active && <CornerDownLeft size={14} className="ap-search-enter" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
